// @flow
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import InterestFollowButton from 'app/common/react/components/Categories/InterestFollowButton';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import ProfileEmptyState from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyState';
import ProfileEmptyStateText from 'app/common/react/components/profile/ProfileEmptyState/ProfileEmptyStateText';
import ProfileGridLayout from 'app/common/react/components/profile/ProfileGridLayout/ProfileGridLayout';
import TopicRep from 'app/common/react/components/common/TopicRep/TopicRep';
import type { ProfileTopic } from 'app/packages/pinterest-flow-types/topic';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import urlUtil from 'app/common/lib/urlUtil';
import withResource from 'app/common/lib/react/withResource';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import withViewer, { type Viewer } from 'app/common/lib/react/withViewer';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Icon, Mask, ScrollFetch, Spinner, Text } from 'gestalt';
import { stopTiming, logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';

type ProfileEntryProps = {
  ...ProfileTopic,
  primaryAction?: boolean,
  requestContext: RequestContextType,
};

type Props = {
  hideExploreButton?: boolean,
  primaryAction?: boolean,
  requestContext: RequestContextType,
  topicsResource: {
    data: Array<ProfileEntryProps>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
  },
  // indicate the interesting follow button is
  // triggered from Profile page Topic tab
  user: ProfileUser,
  viewer: Viewer,
};

export function FindTopicsRep({ onClick }: { onClick: () => void }) {
  return (
    <Box marginLeft={-6} padding={6}>
      <NavigateLink to="/categories" onClick={onClick}>
        <Box>
          <Mask height={236} shape="rounded" wash width={236}>
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Icon
                accessibilityLabel={i18n._(
                  'add topics',
                  'accessible label for Explore topics add icon',
                )}
                color="red"
                icon="add-circle"
                size={40}
              />
            </Box>
          </Mask>
          <Box paddingY={3}>
            <Text color="gray" lgSize="xl" mdSize="lg" size="xl" smSize="md" weight="bold">
              {i18n._('Explore topics', 'Text to display for link to find topics to follow')}
            </Text>
          </Box>
        </Box>
      </NavigateLink>
    </Box>
  );
}

function sanitizeTopicUrl(url: string) {
  const localeSeparatorIndex = 2;
  let sanitizedUrl = url;
  if (url.includes(':') && url.indexOf(':') === localeSeparatorIndex) {
    sanitizedUrl = url.substring(localeSeparatorIndex + 1);
  }
  return sanitizedUrl;
}

function renderTopic(topic: ProfileEntryProps) {
  const {
    id = '',
    images = {},
    name = '',
    background_color: backgroundColor = '',
    is_followed: followedByMe = false,
    url_name: url = '',
    primaryAction = false,
  } = topic;

  return (
    <Box key={id} marginLeft={-6} padding={6}>
      <TopicRep
        backgroundColor={backgroundColor}
        id={id}
        image={images['400x']}
        followedByMe={followedByMe}
        name={name}
        url={sanitizeTopicUrl(url)}
        followingButton={
          Boolean(primaryAction) && (
            <InterestFollowButton
              fullWidth
              topicId={id}
              isFollowed={followedByMe}
              primaryAction={Boolean(primaryAction)}
            />
          )
        }
      />
    </Box>
  );
}

export class FollowingTopicsContainer extends PureComponent<Props> {
  componentDidUpdate(prevProps: Props) {
    const { viewer, user, topicsResource } = this.props;
    if (!prevProps.topicsResource.isLoaded && topicsResource.isLoaded) {
      const isOwnProfile = viewer.isAuth && viewer.username === user.username;
      logCustomTraceEvent({ name: 'MasonryDidMount' });
      logCustomTraceEvent({
        name: 'UserNavRegisterGridImagesTraceEvent',
        viewportFilled: false,
        gridData: topicsResource.data,
        el: ReactDOM.findDOMNode(this),
      });
      logCustomTraceEvent({ name: 'UserNavRegisterGridItemsPositionedTraceEvent' });
      stopTiming(
        isOwnProfile
          ? PCONST.thrift.TracingClientTypes.PWTAction.OWN_PROFILE
          : PCONST.thrift.TracingClientTypes.PWTAction.OTHER_PROFILE,
      );
    }
  }

  handleFindTopicsClick = () => {
    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.USER,
      viewParameter: PCONST.thrift.ViewParameterType.USER_FOLLOWING,
      element: PCONST.thrift.ElementType.SEARCH_INTERESTS_BUTTON,
    });
  };

  render() {
    const {
      viewer,
      topicsResource: { data, fetchMore, isAtEnd, isFetching, isLoaded },
      user,
      hideExploreButton,
      primaryAction,
      requestContext,
    } = this.props;
    const isOwnProfile = viewer.isAuth && viewer.username === user.username;
    return (
      <Box>
        <ScrollFetch isAtEnd={isAtEnd} isFetching={isFetching} fetchMore={fetchMore} />
        {isLoaded &&
          (data.length ? (
            <ProfileGridLayout itemWidth={260}>
              {!hideExploreButton &&
                isOwnProfile && <FindTopicsRep onClick={this.handleFindTopicsClick} />}
              {data
                .map((val) => Object.assign(val, { primaryAction, requestContext }))
                .map(renderTopic)}
            </ProfileGridLayout>
          ) : (
            <ProfileEmptyState>
              <ProfileEmptyStateText subject="topics" surface="following" user={user} />
            </ProfileEmptyState>
          ))}
        <Spinner
          accessibilityLabel={i18n._(
            'Loading additional topics',
            'Label for the loading state spinner in the Topics section of the Following page',
          )}
          show={isFetching || !isLoaded}
        />
      </Box>
    );
  }
}

export const FollowingTopicsNuxContainer = compose(
  withRequestContext,
  withViewer,
  connect(
    (): {
      hideExploreButton: boolean,
      primaryAction: boolean,
    } => ({
      hideExploreButton: true,
      primaryAction: true,
    }),
  ),
  withResource({
    name: 'NuxInterestsResource',
    key: 'topicsResource',
    options: {
      is_renux: true,
      isLazyLoading: true,
      urlParams: urlUtil.getQueryStringParams(),
    },
  }),
)(FollowingTopicsContainer);

export default compose(
  withRequestContext,
  withViewer,
  withResource({
    name: 'InterestFollowingResource',
    key: 'topicsResource',
    options: ({ user }) => ({
      username: user.username,
    }),
  }),
)(FollowingTopicsContainer);
